import { CoachingSessionStatus } from '../../../enums/coaching.enum';
import { GroupStatus } from '../../../enums/enums';
import { SPECIFIC_COHORT_ID } from '../../../features/learning/constants';
import { type ICoachingSessionFullDTO } from '../../../models/CoachingSessionFullDTO';
import { baseGroups, baseSessions } from '../fixtures/sessions';
import { clone } from '../support/clone';
import { nowIso } from '../support/date-utils';
import { getScenario } from '../support/scenarios';
import { readStorage, storageKeys, writeStorage } from '../support/storage';

const personaIdMap: Record<string, string> = {
  mentee: 'dev-user-1',
  coach: 'dev-co-leader-1',
  leader: 'dev-leader-1',
  new: 'dev-new-noah',
  veteran: 'dev-veteran-val'
};

const personaPrimaryGroupIdMap: Record<string, string> = {
  mentee: 'dev-group-1',
  coach: 'dev-group-4',
  leader: 'dev-group-1',
  new: 'dev-group-1',
  veteran: 'dev-group-complete'
};

function getProgramLoadForPersona(persona: string): string {
  const storedProgramLoad = getScenario('program_load', 'single');

  // WHY: Veteran Val's defining story is multiple completed cohorts.
  // Single-load would hide all but her primary cohort, so we force multi-load for her persona.
  return persona === 'veteran' && storedProgramLoad === 'single' ? 'multiple' : storedProgramLoad;
}

function isPersonaVisibleSession(session: ICoachingSessionFullDTO, persona: string): boolean {
  const activePersonaId = personaIdMap[persona] || personaIdMap.mentee;
  const group = session.groupDto ?? session.group;
  const participantIds = group?.participantIds?.split(',') || [];
  const isParticipant = participantIds.includes(activePersonaId) || session.participantId === activePersonaId;
  const isMentor = persona === 'coach' || persona === 'leader';

  if (isMentor) {
    return group?.leaderId === activePersonaId || group?.coLeaderId === activePersonaId || isParticipant;
  }

  return isParticipant;
}

function filterSessionsForScenario(sessions: ICoachingSessionFullDTO[]) {
  const persona = getScenario('persona', 'mentee');
  const hygiene = getScenario('hygiene', 'standard');
  const programLoad = getProgramLoadForPersona(persona);

  let filteredSessions = sessions.filter((session) => isPersonaVisibleSession(session, persona));

  if (programLoad === 'none') {
    return [];
  }

  if (programLoad === 'single') {
    const singleGroupId =
      hygiene === 'cohort_complete' ? 'dev-group-complete' : personaPrimaryGroupIdMap[persona] || 'dev-group-1';
    filteredSessions = filteredSessions.filter((session) => session.groupId === singleGroupId);
  }

  if (hygiene === 'cohort_complete') {
    filteredSessions = filteredSessions.filter((session) => session.groupId === 'dev-group-complete');
  }

  return filteredSessions;
}

function readStoredSessionsWithBackfill(): ICoachingSessionFullDTO[] {
  let sessions = readStorage(storageKeys.sessions, baseSessions);
  const storedIds = new Set(sessions.map((session) => session.id));
  const missingFixtures = baseSessions.filter((session) => !storedIds.has(session.id));

  if (missingFixtures.length > 0) {
    sessions = [...sessions, ...missingFixtures];
  }

  return sessions;
}

// --- Public Domain API ---

/**
 * Returns mock groups.
 */
export function getDevMockGroups() {
  const scenario = getScenario('sessions', 'standard');
  const visibleSessions = scenario === 'empty' ? [] : filterSessionsForScenario(readStoredSessionsWithBackfill());
  const visibleGroupIds = new Set(visibleSessions.map((session) => session.groupId));
  const currentGroupIds = new Set(
    visibleSessions
      .filter((session) => session.status === CoachingSessionStatus.Confirmed)
      .map((session) => session.groupId)
  );

  const groups = baseGroups
    .filter((group) => visibleGroupIds.has(group.id))
    .map((group) => ({
      ...group,
      status: currentGroupIds.has(group.id) ? GroupStatus.Active : GroupStatus.Inactive
    }));

  // WHY: The production Workshop Library gate checks this cohort ID. A synthetic membership
  // keeps the gated section visible in dev preview without weakening the production gate.
  if (import.meta.env.DEV && visibleSessions.length > 0) {
    return [
      ...groups,
      {
        id: SPECIFIC_COHORT_ID,
        description: 'Dev Preview Workshop Library',
        status: GroupStatus.Active
      }
    ];
  }

  return groups;
}

/**
 * Returns mock sessions, potentially adjusted by scenario and time shift.
 */
export function getDevMockSessions() {
  const scenario = getScenario('sessions', 'standard');
  const hygiene = getScenario('hygiene', 'standard');

  let sessions = readStoredSessionsWithBackfill();

  sessions = filterSessionsForScenario(sessions);

  // WHY: Support time-shifted demos without modifying baseline fixture dates
  const shiftMins = parseInt(getScenario('time_shift_mins', '0'), 10);
  let adjustedSessions = sessions.map((s) => {
    const d = new Date(s.date);
    d.setMinutes(d.getMinutes() - shiftMins);
    return { ...s, date: d.toISOString() };
  });

  // WHY: Hygiene Modulators
  if (hygiene === 'passed') {
    // Force "today" sessions into the past to test completion/overdue states
    adjustedSessions = adjustedSessions.map((s) => {
      if (s.id.includes('today')) {
        const d = new Date();
        d.setHours(d.getHours() - 1);
        return { ...s, date: d.toISOString() };
      }
      return s;
    });
  }

  if (hygiene === 'cohort_complete') {
    adjustedSessions = adjustedSessions.filter((s) => s.groupId === 'dev-group-complete');
  }

  if (scenario === 'empty') return [];

  if (scenario === 'heavy') {
    // WHY: Quad load for heavy list performance audits and stress testing UI components
    return [...adjustedSessions, ...adjustedSessions, ...adjustedSessions, ...adjustedSessions].map((s, idx) => ({
      ...s,
      id: `${s.id}-${idx}`
    }));
  }

  return adjustedSessions;
}

/**
 * Updates a mock session.
 */
export function updateDevMockSession(sessionId: string, updates: Partial<ICoachingSessionFullDTO>) {
  // WHY: Bypasses the multiplier transform in getDevMockSessions() to avoid saving Heavy data back into the Base list.
  const currentSessions = readStorage(storageKeys.sessions, baseSessions);
  const nextSessions = currentSessions.map((session) =>
    session.id === sessionId ? { ...session, ...updates, updatedAt: nowIso() } : session
  );

  writeStorage(storageKeys.sessions, nextSessions);
  return nextSessions.find((session) => session.id === sessionId) ?? null;
}

/**
 * Resets mock sessions to baseline.
 */
export function resetDevMockSessions() {
  return writeStorage(storageKeys.sessions, clone(baseSessions));
}
